<template>
  <van-popup
    :value="value"
    position="bottom"
    round
    :close-on-click-overlay="true"
    class="date-picker-popup"
    @input="$emit('input', $event)"
  >
    <div class="dp">
      <!-- Handle bar -->
      <div class="dp__bar">
        <span class="dp__bar-line"></span>
      </div>

      <!-- Title -->
      <h3 class="dp__title">{{ title || 'Select Date' }}</h3>
      <p class="dp__subtitle">{{ subtitle || 'Select a period to filter your history' }}</p>

      <!-- Picker -->
      <van-picker
        ref="picker"
        :columns="columns"
        :default-index="defaultIndexes"
        :visible-item-count="3"
        :item-height="44"
        @change="onPickerChange"
      />

      <!-- Confirm Button -->
      <div class="dp__actions">
        <button class="dp__confirm" @click="onConfirm">
          {{ confirmText || 'Confirm Selection' }}
        </button>
        <button class="dp__cancel" @click="onCancel">
          {{ cancelText || 'Cancel' }}
        </button>
      </div>
    </div>
  </van-popup>
</template>

<script>
export default {
  name: 'DatePickerPopup',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    subtitle: {
      type: String,
      default: ''
    },
    confirmText: {
      type: String,
      default: ''
    },
    cancelText: {
      type: String,
      default: ''
    },
    minDate: {
      type: Date,
      default: () => new Date(2020, 0, 1)
    },
    maxDate: {
      type: Date,
      default: () => new Date()
    },
    defaultDate: {
      type: Date,
      default: null
    }
  },
  data() {
    return {
      currentYear: 0,
      currentMonth: 0,
      currentDay: 0
    }
  },
  computed: {
    normalizedRange() {
      const fallbackMin = new Date(2020, 0, 1)
      const fallbackMax = new Date()
      let min = this.toValidDate(this.minDate, fallbackMin)
      let max = this.toValidDate(this.maxDate, fallbackMax)

      min = this.startOfDay(min)
      max = this.startOfDay(max)

      if (max.getTime() < min.getTime()) {
        const temp = min
        min = max
        max = temp
      }

      return { min, max }
    },
    years() {
      const arr = []
      const { min, max } = this.normalizedRange
      for (let y = min.getFullYear(); y <= max.getFullYear(); y++) {
        arr.push(y)
      }
      return arr
    },
    months() {
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ]
      let start = 0
      let end = 11
      const { min, max } = this.normalizedRange
      const currentYear = this.clampNumber(this.currentYear, min.getFullYear(), max.getFullYear())
      if (currentYear === min.getFullYear()) {
        start = min.getMonth()
      }
      if (currentYear === max.getFullYear()) {
        end = max.getMonth()
      }
      const arr = []
      for (let m = start; m <= end; m++) {
        arr.push({ value: m, text: monthNames[m] })
      }
      return arr
    },
    days() {
      const { min, max } = this.normalizedRange
      const currentYear = this.clampNumber(this.currentYear, min.getFullYear(), max.getFullYear())
      const months = this.months
      const fallbackMonth = months.length > 0 ? months[0].value : min.getMonth()
      const currentMonth = months.some(month => month.value === this.currentMonth) ? this.currentMonth : fallbackMonth
      const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()
      let start = 1
      let end = daysInMonth
      if (currentYear === min.getFullYear() && currentMonth === min.getMonth()) {
        start = min.getDate()
      }
      if (currentYear === max.getFullYear() && currentMonth === max.getMonth()) {
        end = Math.min(end, max.getDate())
      }
      const arr = []
      for (let d = start; d <= end; d++) {
        arr.push(d)
      }
      return arr
    },
    columns() {
      const yearIndex = this.safeIndex(this.years.indexOf(this.currentYear), this.years)
      const monthIndex = this.safeIndex(this.months.findIndex(m => m.value === this.currentMonth), this.months)
      const dayIndex = this.safeIndex(this.days.indexOf(this.currentDay), this.days)
      return [
        { values: this.years.map(String), defaultIndex: yearIndex },
        { values: this.months.map(m => m.text), defaultIndex: monthIndex },
        { values: this.days.map(String), defaultIndex: dayIndex }
      ]
    },
    defaultIndexes() {
      return [
        this.safeIndex(this.years.indexOf(this.currentYear), this.years),
        this.safeIndex(this.months.findIndex(m => m.value === this.currentMonth), this.months),
        this.safeIndex(this.days.indexOf(this.currentDay), this.days)
      ]
    }
  },
  watch: {
    value(val) {
      if (val) {
        this.initDate()
      }
    }
  },
  created() {
    this.initDate()
  },
  methods: {
    toValidDate(value, fallback) {
      return value instanceof Date && !Number.isNaN(value.getTime()) ? new Date(value.getTime()) : new Date(fallback.getTime())
    },
    startOfDay(date) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate())
    },
    clampNumber(value, min, max) {
      const number = Number.isFinite(value) ? value : min
      return Math.min(Math.max(number, min), max)
    },
    safeIndex(index, values) {
      return index >= 0 && values.length > 0 ? index : 0
    },
    getPickerMethod(picker, name) {
      return picker && typeof picker[name] === 'function' ? picker[name].bind(picker) : null
    },
    clampDate(date) {
      const { min, max } = this.normalizedRange
      const value = this.startOfDay(this.toValidDate(date, max))
      if (value.getTime() < min.getTime()) return new Date(min.getTime())
      if (value.getTime() > max.getTime()) return new Date(max.getTime())
      return value
    },
    applySafeCurrentDate(date) {
      const safeDate = this.clampDate(date)
      this.currentYear = safeDate.getFullYear()
      this.currentMonth = safeDate.getMonth()
      this.currentDay = safeDate.getDate()
      this.clampCurrentParts()
    },
    clampCurrentParts() {
      const years = this.years
      if (!years.includes(this.currentYear)) {
        this.currentYear = years[this.safeIndex(years.indexOf(this.currentYear), years)]
      }

      const months = this.months
      if (!months.some(month => month.value === this.currentMonth)) {
        this.currentMonth = months[this.safeIndex(-1, months)].value
      }

      const days = this.days
      if (!days.includes(this.currentDay)) {
        this.currentDay = days[days.length - 1]
      }
    },
    initDate() {
      const { max } = this.normalizedRange
      this.applySafeCurrentDate(this.toValidDate(this.defaultDate, max))
    },
    onPickerChange(picker, values) {
      const selectedValues = Array.isArray(values) ? values : []
      const yearVal = parseInt(selectedValues[0])
      const monthText = selectedValues[1]
      const dayVal = parseInt(selectedValues[2])

      if (this.years.includes(yearVal)) {
        this.currentYear = yearVal
      }

      // Find the month value from text
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ]
      const monthIdx = monthNames.indexOf(monthText)
      if (monthIdx !== -1) {
        this.currentMonth = monthIdx
      }

      // Update month column based on year
      const monthCol = this.months.map(m => m.text)
      const setColumnValues = this.getPickerMethod(picker, 'setColumnValues')
      const setColumnIndex = this.getPickerMethod(picker, 'setColumnIndex')
      if (setColumnValues) setColumnValues(1, monthCol)

      // Clamp month if out of range
      const currentMonthInList = this.months.find(m => m.value === this.currentMonth)
      if (!currentMonthInList && this.months.length > 0) {
        this.currentMonth = this.months[this.months.length - 1].value
        if (setColumnIndex) setColumnIndex(1, this.months.length - 1)
      }

      // Update day column based on year + month
      const dayCol = this.days.map(String)
      if (setColumnValues) setColumnValues(2, dayCol)

      // Clamp day if out of range
      if (Number.isInteger(dayVal) && this.days.includes(dayVal)) {
        this.currentDay = dayVal
      } else {
        const dayIndex = this.days.length - 1
        this.currentDay = this.days[dayIndex]
        if (setColumnIndex) setColumnIndex(2, dayIndex)
      }
    },
    onConfirm() {
      const date = new Date(this.currentYear, this.currentMonth, this.currentDay)
      this.$emit('confirm', date)
      this.$emit('input', false)
    },
    onCancel() {
      this.$emit('cancel')
      this.$emit('input', false)
    }
  }
}
</script>

<style lang="less" scoped>
@primary: @brand;      /* 柠檬绿→火焰橙(选中/激活) */
@bg-dark: @vk-bg;      /* 深页面底 */
@surface: @vk-surface; /* 卡片面 */

.dp {
  background: @bg-dark;
  padding: 16px 20px 24px;
  border-radius: 16px 16px 0 0;

  &__bar {
    display: flex;
    justify-content: center;
    padding-bottom: 16px;
  }

  &__bar-line {
    width: 40px;
    height: 4px;
    background: #334155;
    border-radius: 2px;
  }

  &__title {
    color: #ffffff;
    font-size: var(--vk-fs-lg);
    font-weight: bold;
    text-align: center;
    margin-bottom: 4px;
  }

  &__subtitle {
    color: var(--vk-t3);
    font-size: var(--vk-fs-xs);
    text-align: center;
    margin-bottom: 20px;
  }

  &__actions {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    align-items: center;
  }

  &__confirm {
    width: 100%;
    background: @primary;
    color: @bg-dark;
    font-size: var(--vk-fs-md);
    font-weight: 900;
    padding: 14px;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    text-transform: uppercase;
    letter-spacing: 0.02em;

    &:active {
      opacity: 0.9;
      transform: scale(0.98);
    }
  }

  &__cancel {
    background: transparent;
    border: none;
    color: var(--vk-t2);
    font-size: var(--vk-fs-base);
    font-weight: 600;
    cursor: pointer;
    padding: 8px 16px;

    &:active {
      color: #ffffff;
    }
  }
}
</style>

<style lang="less">
// Unscoped - override Vant picker styles for dark theme
.date-picker-popup {
  background: #0A0E1A !important;
  overflow: hidden;

  .van-picker {
    background: transparent !important;
  }

  .van-picker-column__wrapper {
    .van-picker-column__item {
      color: #475569 !important;
      font-size: var(--vk-fs-md);
      font-weight: 500;

      &--selected {
        color: var(--vk-brand) !important;
        font-size: var(--vk-fs-xl);
        font-weight: bold;
      }
    }
  }

  // The highlight frame around selected item
  .van-picker__frame {
    border: 2px solid rgba(255, 90, 31, 0.3) !important;
    border-radius: 12px;
    background: rgba(255, 90, 31, 0.05);

    &::after {
      display: none;
    }
  }

  // Top and bottom masks (gradient overlays)
  .van-picker__mask {
    background-image: none !important;
    background: transparent !important;
  }

  // Remove default hairlines
  .van-hairline--top-bottom::after {
    display: none;
  }

  .van-picker__toolbar {
    display: none;
  }

  .van-picker__columns {
    background: transparent;
  }
}
</style>
